import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { BASE_URL } from "../config";
import axios from 'axios';
import Logo from "../assets/images/cftLogo.svg"
import calendarIcon from "../assets/images/calendarIcon.svg"
import commentIcon from "../assets/images/commentIcon.svg"
import goalIcon from "../assets/images/goalIcon.svg"
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import authStorage from "../utils/authStorage";
import moment from "moment";
import ContentModal from "../components/contentModal";

const UserSignUp = () => {
    const params = useParams();

    const [customerForm, setCustomerForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        typeOfCompany: "",
        company:"",
        dob: "",
        gender: "",
        height: "",
        weight: "",
        medicalConsent: false,
        termsAccepted: false
    });
    const [consentContent, setConsentContent] = useState({ title: "", content: "" });
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [showInvalidLink, setShowInvalidLink] = useState(false);
    const [loading, setLoading] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordc, setShowPasswordc] = useState(false); 
    const [submitLoading, setSubmitLoading] = useState('');
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('Password should not be empty');
    const [confirmPasswordError, setConfirmPasswordError] = useState('Confirm password should not be empty');
    const [formTouched, setFormTouched] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        setCustomerForm({ ...customerForm, password: value });

        if (!passwordRegex.test(value)) {
            setPasswordError('Password must contain at least one digit, one lowercase letter, one uppercase letter, and be at least 8 characters long.');
        } else {
            setPasswordError('');
        }
    };

    const handleConfirmPasswordChange = (event) => {
        const value = event.target.value;
        setCustomerForm({ ...customerForm, confirmPassword: value });
        if (customerForm?.password !== value) {
            setConfirmPasswordError('Confirm password must match password');
        } else {
            setConfirmPasswordError('');
        }
    };

    const verifyUser = () => {
        setError("");
        setLoading(true);
        axios({
            url: `${BASE_URL}/auth/invite/user/${params?.token}`, //your url
            method: 'get'
        }).then((res) => {
            setLoading(false);
            let userData = res?.data?.data;
            setCustomerForm(prev => ({ ...prev, firstName: userData?.firstName, lastName: userData?.lastName, email: userData?.email, company: userData?.compId }))
        }).catch((err) => {
            setShowInvalidLink(true);
            if (err?.response?.data?.message) {
                setError(err?.response?.data?.message);
            }
            setLoading(false);
        });
    };

    const handleSubmit = (event) => {
        setFormTouched(true);
        if (passwordError || confirmPasswordError || !customerForm?.firstName || !customerForm?.lastName) {
            return;
        };
        if (!customerForm?.dob || !customerForm?.gender || !customerForm?.height || !customerForm?.weight) {
            setError("Please fill in date of birth, sex, height and weight.");
            setTimeout(() => setError(""), 4000);
            return;
        }
        if (moment().diff(moment(customerForm.dob), 'years') < 18) {
            setError("You must be 18 years or older to register.");
            setTimeout(() => setError(""), 4000);
            return;
        }
        if (!customerForm?.medicalConsent) {
            setError("Please confirm the medical consent to continue.");
            setTimeout(() => setError(""), 4000);
            return;
        }
        if (!customerForm?.termsAccepted) {
            setError("Please accept the Terms and Conditions to continue.");
            setTimeout(() => setError(""), 4000);
            return;
        }
        setError("");
        setSubmitLoading(true);
        axios({
            url: `${BASE_URL}/auth/add/user`, //your url
            method: 'POST',
            data: customerForm
        }).then((res) => {
            setSubmitLoading(false);
            setTimeout(() => {
                navigate(`/company/user/signup/success/${customerForm?.firstName}`);
            }, 200);
        }).catch((err) => {
            if (err?.response?.data?.message) {
                setError(err?.response?.data?.message);
                setTimeout(() => {
                    setError("");
                }, 3000);
            }
            setSubmitLoading(false);
        });
    };

    useEffect(() => {
        verifyUser();
    }, []);

    // Load the editable medical-condition disclaimer shown in the "Read more" popup.
    useEffect(() => {
        axios.get(`${BASE_URL}/content/medicalConsent`)
            .then((res) => {
                if (res?.data?.data) {
                    setConsentContent({ title: res.data.data.title, content: res.data.data.content });
                }
            })
            .catch((err) => console.log("Failed to load consent content", err));
    }, []);

    return (
        <>
            <ContentModal
                show={showConsentModal}
                onClose={() => setShowConsentModal(false)}
                title={consentContent.title || "Health & Medical Disclaimer"}
                content={consentContent.content}
            />
            <div className="sign-in-default sign-in-default-v1 sign-up-page">
                <div className="container">
                    {loading && (
                        <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
                            <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    )}
                    {!loading && <> {showInvalidLink ? (
                        <p className="text-red-500">Invalid Link</p>
                    ) : (
                        <div className="form-box-outer">

                            <div className="content-block">
                                <div className="logo">
                                    <img src={Logo} alt="logo" />
                                </div>
                                <h1>CFT is your all-in-one <br /> fitness solution</h1>
                                <p>CFT is a fitness tracker-based App that provides all the necessary components to help individuals and employees become healthy.</p>
                                <ul>
                                    <li><img src={calendarIcon} /> Monthly Calendar</li>
                                    <li><img src={goalIcon} /> Summary And Goals</li>
                                    <li><img src={commentIcon} /> Healthy content</li>
                                </ul>
                                <span className="join-us">Join us today</span>
                            </div>

                            <div className="form-box">
                                <h2>Company Signup</h2>
                            <div className="flex items-center justify-center pb-8">
                                <form style={{ zIndex: '80000' }} className="md:w-[40vw] w-[80vw]" action="#" method="POST">
                                    <input type="hidden" name="remember" value="true" />
                                    <div className="rounded-md">
                                        <div className="input-box">
                                            <label htmlFor="firstName" className="sr-only">First Name</label>
                                            <input
                                                value={customerForm?.firstName}
                                                onChange={(e) => setCustomerForm({ ...customerForm, firstName: e.target.value })}
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                className=""
                                                placeholder="First Name"
                                            />
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="lastName" className="sr-only">Last Name</label>
                                            <input
                                                value={customerForm?.lastName}
                                                onChange={(e) => setCustomerForm({ ...customerForm, lastName: e.target.value })}
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                className=""
                                                placeholder="Last Name"
                                            />
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="email-address" className="sr-only">Email</label>
                                            <input
                                                value={customerForm?.email}
                                                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                                                id="email-address"
                                                name="email"
                                                type="email"
                                                disabled
                                                required
                                                className=""
                                                placeholder="Email"
                                            />
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="password" className="sr-only">Password</label>
                                            <div className="relative">
                                            <input
                                                value={customerForm?.password}
                                                onChange={(e) => handlePasswordChange(e)}
                                                id="password"
                                                type={!showPassword?'password':"text"}
                                                required
                                                className=""
                                                placeholder="Password"
                                            />
                                                <span className="input-icon" onClick={()=>{setShowPassword(!showPassword)}} >
                                                    {!showPassword?<EyeSlashIcon class="h-5 w-5 text-gray-300" />: <EyeIcon class="h-5 w-5 text-gray-300" />}
                                                </span>
                                                </div>
                                            {formTouched && <span className="text-red-500 text-xs mt-1 block">{passwordError}</span>}
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="password" className="sr-only">Confirm Password</label>
                                            <div className="relative">
                                            <input
                                                value={customerForm?.confirmPassword}
                                                onChange={(e) => handleConfirmPasswordChange(e)}
                                                id="password"
                                                name="password"
                                                type={!showPasswordc?'password':"text"}
                                                autoComplete="current-password"
                                                required
                                                className=""
                                                placeholder="Confirm Password"
                                            />
                                                <span className="input-icon" onClick={()=>{setShowPasswordc(!showPasswordc)}} >
                                                    {!showPasswordc?<EyeSlashIcon class="h-5 w-5 text-gray-300" />: <EyeIcon class="h-5 w-5 text-gray-300" />}
                                                </span>
                                            </div>
                                            {formTouched && <span className="text-red-500 text-xs mt-1 block">{confirmPasswordError}</span>}
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="dob">Date of Birth<span className="text-red-500"> *</span></label>
                                            <input
                                                value={customerForm?.dob || ""}
                                                onChange={(e) => setCustomerForm({ ...customerForm, dob: e.target.value })}
                                                id="dob"
                                                name="dob"
                                                type="date"
                                                max={moment().subtract(18, 'years').format('YYYY-MM-DD')}
                                                min={moment().subtract(100, 'years').format('YYYY-MM-DD')}
                                            />
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="gender">Sex<span className="text-red-500"> *</span></label>
                                            <select
                                                value={customerForm?.gender || ""}
                                                onChange={(e) => setCustomerForm({ ...customerForm, gender: e.target.value })}
                                                id="gender"
                                                name="gender"
                                            >
                                                <option value="">Select Sex*</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <div className="input-box">
                                                <label htmlFor="height">Height (cm)<span className="text-red-500"> *</span></label>
                                                <input
                                                    value={customerForm?.height || ""}
                                                    onChange={(e) => setCustomerForm({ ...customerForm, height: e.target.value })}
                                                    id="height"
                                                    name="height"
                                                    type="number"
                                                    min="0"
                                                    placeholder="Height in cm"
                                                />
                                            </div>
                                            <div className="input-box">
                                                <label htmlFor="weight">Weight (kg)<span className="text-red-500"> *</span></label>
                                                <input
                                                    value={customerForm?.weight || ""}
                                                    onChange={(e) => setCustomerForm({ ...customerForm, weight: e.target.value })}
                                                    id="weight"
                                                    name="weight"
                                                    type="number"
                                                    min="0"
                                                    placeholder="Weight in kg"
                                                />
                                            </div>
                                        </div>
                                        <div className="input-box">
                                            <label htmlFor="medicalConsent" className="cursor-pointer flex items-start">
                                                <input
                                                    type="checkbox"
                                                    checked={customerForm?.medicalConsent || false}
                                                    onChange={(e) => setCustomerForm({ ...customerForm, medicalConsent: e.target.checked })}
                                                />
                                                <span style={{ marginBottom: 0 }}>
                                                    I confirm that if I have any medical condition (such as a heart condition, high blood pressure, diabetes, etc.), I have consulted my physician before participating.<span className="text-red-500"> *</span>{" "}
                                                    <span
                                                        className="cursor-pointer underline"
                                                        style={{ color: "#4285F4", fontWeight: 500 }}
                                                        onClick={(e) => { e.preventDefault(); setShowConsentModal(true); }}
                                                    >
                                                        Read more
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <div className="input-box px-8">
                                <label htmlFor="termsAccepted" className="cursor-pointer flex items-start">
                                    <input
                                        type="checkbox"
                                        checked={customerForm?.termsAccepted || false}
                                        onChange={(e) => setCustomerForm({ ...customerForm, termsAccepted: e.target.checked })}
                                    />
                                    <span style={{ marginBottom: 0 }}>
                                        I have read and agree to the
                                        <a href="https://beta.contingentfitness.com/terms-and-conditions/" className="font-medium text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"> Terms and Conditions</a> and
                                        <a href="https://beta.contingentfitness.com/privacy-policy/" className="font-medium text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"> Privacy Policy</a>.<span className="text-red-500"> *</span>
                                    </span>
                                </label>
                            </div>
                            <div className="text-red-500 pt-2 text-center h-8">{error}</div>
                            <div className="flex justify-center">
                                <button onClick={() => handleSubmit()} type="button" className="group relative flex w-40 justify-center rounded-md border border-transparent bg-[#4285F4] py-2 px-4 text-sm font-medium text-white hover:bg-[#4285F4] focus:outline-none">
                                    {loading ? (<div className="lds-facebook"><div></div><div></div><div></div><div></div><div></div></div>) : "Register"}
                                </button>
                            </div>

                        </div>

                        </div>
                    )}
                    </>
                    }
                </div >
            </div >
        </>
    )
}
export default UserSignUp;